import { FC, useContext, useEffect } from "react";
import { Stack, Text, Box, Flex, useMantineTheme } from "@mantine/core";
import { CompetitionContext } from "../../../../CompetitionContext.tsx";
import { getPresentationParamsForOrigin } from "../../../../getPresentationParamsForOrigin.ts";
import { useNavigate, useParams } from "react-router-dom";
import { useCompetition } from "../../../../hooks/useCompetition.ts";
import { useLeaderboard } from "../../../../hooks/useLeaderboard.ts";
import { ResolvedTeamReference } from "../../../../Components/ResolvedTeamReference.tsx";
import { TeamLogo } from "../../../../Components/TeamLogo.tsx";
import { TeamName } from "../../../../Components/TeamName.tsx";
import { CenteredLoader } from "../../../../Components/CenteredLoader.tsx";
import { PageTitle } from "../../../../Components/PageTitle.tsx";
import { SectionTitle } from "../../../../Components/SectionTitle.tsx";
import { IconTrophy } from "@tabler/icons-react";
import { components } from "../../../../openapi/generated-types.ts";
import { CardContainer } from "../../../../Components/CardContainer.tsx";
import { ListRow } from "../../../../Components/ListRow.tsx";
import { EmptyState } from "../../../../Components/EmptyState.tsx";
import { useMetaDescription } from "../../../../hooks/useMetaDescription.ts";

const LeaderboardTable: FC<{
  competitionId: string;
  groupId: string;
}> = ({ competitionId, groupId }) => {
  const theme = useMantineTheme();

  const leaderboardData = useLeaderboard(competitionId, groupId);

  if (!leaderboardData.isSuccess) {
    return <CenteredLoader />;
  }

  const teams = leaderboardData.data.leaderboard;

  if (teams.length === 0) {
    return <EmptyState message="Noch keine Mannschaften" />;
  }

  return (
    <Box>
      {/* Header Row */}
      <Flex align="center" py={4} px="xs" gap="xs">
        <Text size="xs" c="dimmed" w={20} ta="center">
          #
        </Text>
        <Box w={24} />
        <Text size="xs" c="dimmed" style={{ flex: 1, minWidth: 0 }}>
          Team
        </Text>
        <Text size="xs" c="dimmed" w={20} ta="center">
          S
        </Text>
        <Text size="xs" c="dimmed" w={40} ta="center">
          Tore
        </Text>
        <Text size="xs" c="dimmed" w={28} ta="center">
          TD
        </Text>
        <Text size="xs" c="dimmed" w={20} ta="right">
          P
        </Text>
      </Flex>

      {teams.map((team, index) => {
        const isFirst = index === 0;
        const isLast = index === teams.length - 1;
        const highlightColor =
          theme.colors.fwcBlue?.[0] ?? theme.colors.blue[0];

        return (
          <ListRow
            key={team.teamId}
            isFirst={isFirst}
            isLast={isLast}
            highlighted={isFirst}
            highlightColor={highlightColor}
            to={`/teams/${team.teamId}`}
          >
            {/* Position */}
            <Text
              fw={700}
              size="sm"
              w={20}
              ta="center"
              c={isFirst ? "fwcBlue.7" : "dimmed"}
            >
              {team.position}
            </Text>

            {/* Team Logo */}
            <Box w={24} style={{ flexShrink: 0 }}>
              <TeamLogo teamId={team.teamId} height={24} />
            </Box>

            {/* Team Name */}
            <Text
              size="sm"
              fw={isFirst ? 600 : 400}
              style={{ flex: 1, minWidth: 0 }}
              lineClamp={1}
            >
              <TeamName teamId={team.teamId} variant="medium" />
            </Text>

            {/* Games */}
            <Text size="xs" c="dimmed" w={20} ta="center">
              {team.games}
            </Text>

            {/* Goals */}
            <Text
              size="xs"
              c="dimmed"
              w={40}
              ta="center"
              style={{ fontFamily: "Chivo Mono" }}
            >
              {team.scoredGoals}:{team.receivedGoals}
            </Text>

            {/* Goal Difference */}
            <Text
              size="xs"
              w={28}
              ta="center"
              c={
                (team.goalDifference ?? 0) > 0
                  ? "green"
                  : (team.goalDifference ?? 0) < 0
                    ? "red"
                    : "dimmed"
              }
              style={{ fontFamily: "Chivo Mono" }}
            >
              {(team.goalDifference ?? 0) > 0 ? "+" : ""}
              {team.goalDifference ?? 0}
            </Text>

            {/* Points */}
            <Text
              fw={700}
              size="sm"
              c={isFirst ? "fwcBlue.7" : undefined}
              w={20}
              ta="right"
            >
              {team.points}
            </Text>
          </ListRow>
        );
      })}
    </Box>
  );
};

const UnresolvedGroupSlots: FC<{
  slots: components["schemas"]["TeamReference"][];
  competitionId: string;
}> = ({ slots, competitionId }) => {
  return (
    <Box>
      {slots.map((slot, index) => (
        <ListRow
          key={index}
          isFirst={index === 0}
          isLast={index === slots.length - 1}
          gap="sm"
        >
          <Text fw={500} size="sm" w={28} ta="center" c="dimmed">
            {index + 1}
          </Text>
          <Box
            w={28}
            h={28}
            style={{
              borderRadius: "50%",
              background: "var(--mantine-color-gray-2)",
            }}
          />
          <Text size="sm" c="dimmed" style={{ flex: 1 }}>
            <ResolvedTeamReference
              teamReference={slot}
              competitionId={competitionId}
            />
          </Text>
        </ListRow>
      ))}
    </Box>
  );
};

export const LeaderboardPage: FC = () => {
  const { leaderboardPageKey } = useParams<{ leaderboardPageKey: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    if (!leaderboardPageKey) {
      void navigate("/leaderboard/vorrunde", { replace: true });
    }
  }, [leaderboardPageKey, navigate]);

  const competitionId = useContext(CompetitionContext);
  const competition = useCompetition(competitionId);

  const presentationParams = getPresentationParamsForOrigin();
  const leaderboardPages = presentationParams.leaderboardPages;
  const leaderboardDetails = leaderboardPages.find(
    (page) => page.key === (leaderboardPageKey ?? "vorrunde"),
  );

  useMetaDescription(
    leaderboardDetails
      ? `${leaderboardDetails.title} beim ${presentationParams.competitionName} – aktuelle Tabelle mit Punkten, Tordifferenz und Platzierungen.`
      : undefined,
  );

  if (!competition.isSuccess) {
    return (
      <Stack gap="lg" mt="md">
        <CenteredLoader />
      </Stack>
    );
  }

  if (!leaderboardDetails) {
    return (
      <Stack gap="lg" mt="md">
        <Text c="dimmed">Tabelle nicht gefunden</Text>
      </Stack>
    );
  }

  const hasMultipleGroups = leaderboardDetails.groups.length > 1;

  return (
    <Stack gap="lg" mt="md">
      <PageTitle icon={<IconTrophy size={24} />}>
        {leaderboardDetails.title}
      </PageTitle>

      <Text size="sm" c="dimmed">
        Die Tabelle{hasMultipleGroups && "n"} errechne
        {hasMultipleGroups ? "n" : "t"} sich auf Basis der Punkte, Tordifferenz
        und Anzahl geschossener Tore. Bei Gleichstand entscheidet der direkte
        Vergleich und ggf. ein 9-Meter-Schießen.
      </Text>

      {leaderboardDetails.groups.map((groupId) => {
        const group = competition.data.groups.find((g) => g.id === groupId);
        const hasSlots = group?.slots && group.slots.length > 0;
        const isUnresolved = hasSlots && group.teamIds.length === 0;

        return (
          <Box key={groupId}>
            {hasMultipleGroups && (
              <Box mb="xs">
                <SectionTitle>{group?.name ?? "Gruppe"}</SectionTitle>
              </Box>
            )}
            <CardContainer>
              {isUnresolved ? (
                <UnresolvedGroupSlots
                  slots={group.slots}
                  competitionId={competitionId}
                />
              ) : (
                <LeaderboardTable
                  competitionId={competitionId}
                  groupId={groupId}
                />
              )}
            </CardContainer>
          </Box>
        );
      })}
    </Stack>
  );
};
